import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';

const ProductForm = ({ 
  show, 
  onHide, 
  onSubmit, 
  editingProduct = null,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    product_id: '',
    name: '',
    description: '',
    credit_amount: '',
    price: '',
    currency: 'USD',
    is_active: true
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        product_id: editingProduct.product_id || '',
        name: editingProduct.name || '',
        description: editingProduct.description || '',
        credit_amount: editingProduct.credit_amount || '',
        price: editingProduct.price || '',
        currency: editingProduct.currency || 'USD',
        is_active: editingProduct.is_active !== undefined ? editingProduct.is_active : true
      });
    } else {
      setFormData({
        product_id: '',
        name: '',
        description: '',
        credit_amount: '',
        price: '',
        currency: 'USD',
        is_active: true
      });
    }
  }, [editingProduct, show]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Frontend validation
    if (!formData.name.trim()) {
      setError('Product name is required');
      return;
    }
    
    if (formData.name.length > 255) {
      setError('Product name must be less than 255 characters');
      return;
    }
    
    if (formData.product_id && formData.product_id.length > 255) {
      setError('Product ID must be less than 255 characters');
      return;
    }
    
    if (formData.description && formData.description.length > 1000) {
      setError('Product description must be less than 1000 characters');
      return;
    }
    
    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      setError('Price must be a valid positive number');
      return;
    }
    
    if (formData.credit_amount && (isNaN(formData.credit_amount) || parseInt(formData.credit_amount) < 0)) {
      setError('Credit amount must be a valid positive integer');
      return;
    }
    
    if (formData.currency && formData.currency.length !== 3) {
      setError('Currency must be exactly 3 characters');
      return;
    }
    
    // Create data object for submission
    const submitData = {
      product_id: formData.product_id || undefined,
      name: formData.name.trim(),
      description: formData.description || '',
      credit_amount: formData.credit_amount || 0,
      price: formData.price || 0,
      currency: formData.currency,
      is_active: formData.is_active ? 1 : 0
    };

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      product_id: '',
      name: '',
      description: '',
      credit_amount: '',
      price: '',
      currency: 'USD',
      is_active: true
    });
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingProduct ? 'Edit Product' : 'Add Product'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger mb-3">
            {error}
          </div>
        )}
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Product ID</Form.Label>
                <Form.Control
                  type="text"
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  placeholder="Enter unique product ID"
                  maxLength={255}
                />
                <Form.Text className="text-muted">
                  Unique identifier for the product
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Product Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter product name"
                  maxLength={255}
                />
                <Form.Text className="text-muted">
                  {formData.name.length}/255 characters
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter product description"
              maxLength={1000}
            />
            <Form.Text className="text-muted">
              {formData.description.length}/1000 characters
            </Form.Text>
          </Form.Group>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Price</Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Credit Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="credit_amount"
                  value={formData.credit_amount}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Control
                  type="text"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  placeholder="USD"
                  maxLength={3}
                />
                <Form.Text className="text-muted">
                  3-character currency code (e.g., USD, EUR)
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="is_active"
              label="Active"
              checked={formData.is_active}
              onChange={handleInputChange}
            />
            <Form.Text className="text-muted">
              Uncheck to deactivate this product
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            editingProduct ? 'Update' : 'Add'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductForm;
